import React, { useState } from 'react';
import { MaintenanceTicket, TicketStatus, useTickets } from '../context/TicketContext';
import { StatusBadge, SeverityBadge, SlaCountdown } from './StatusBadge';
import { useAuth } from '../context/AuthContext';
import './TicketDrawer.css';

interface Props {
  ticket: MaintenanceTicket | null;
  onClose: () => void;
}

const NEXT_ACTIONS: Record<TicketStatus, { label: string; next: TicketStatus; color: string }[]> = {
  open:        [{ label: 'Start Work',  next: 'in_progress', color: '#60a5fa' }],
  assigned:    [{ label: 'Start Work',  next: 'in_progress', color: '#60a5fa' }],
  in_progress: [{ label: 'Mark Resolved', next: 'resolved', color: '#34d399' }],
  resolved:    [{ label: 'Close Ticket',  next: 'closed',   color: '#6b7280' }],
  closed:      [],
};

const TicketDrawer: React.FC<Props> = ({ ticket, onClose }) => {
  const { technicians, assignTicket, updateStatus, resolveTicket, closeTicket, addNote } = useTickets();
  const { user } = useAuth();
  const actor = user?.email?.split('@')[0] ?? 'User';

  const [noteText,    setNoteText]    = useState('');
  const [resolveText, setResolveText] = useState('');
  const [showResolve, setShowResolve] = useState(false);

  if (!ticket) return null;

  const isResolved = ['resolved', 'closed'].includes(ticket.status);
  const actions    = NEXT_ACTIONS[ticket.status] ?? [];

  const handleAction = (next: TicketStatus) => {
    if (next === 'resolved') { setShowResolve(true); return; }
    if (next === 'closed')   { closeTicket(ticket.ticketId, actor); return; }
    updateStatus(ticket.ticketId, next, actor);
  };

  const handleResolve = () => {
    if (!resolveText.trim()) return;
    resolveTicket(ticket.ticketId, actor, resolveText.trim());
    setShowResolve(false);
    setResolveText('');
  };

  const handleNote = () => {
    if (!noteText.trim()) return;
    addNote(ticket.ticketId, actor, noteText.trim());
    setNoteText('');
  };

  return (
    <div className="drawer-overlay" onClick={onClose}>
      <div className="ticket-drawer" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-title-row">
            <span className="drawer-ticket-id">#{ticket.ticketId.split('_')[1]}</span>
            <SeverityBadge severity={ticket.severity} />
            <StatusBadge   status={ticket.status} />
          </div>
          <h2 className="drawer-title">{ticket.title}</h2>
          <div className="drawer-meta-row">
            <span>🏭 {ticket.machineName}</span>
            {ticket.sensorName && <span>📡 {ticket.sensorName}</span>}
            <SlaCountdown deadline={ticket.slaDeadline} resolved={isResolved} />
          </div>
          <button className="drawer-close" onClick={onClose}>✕</button>
        </div>

        <div className="drawer-body">

          {/* Description */}
          <section className="drawer-section">
            <h4>Description</h4>
            <p className="drawer-desc">{ticket.description}</p>
          </section>

          {/* Assign */}
          {!isResolved && (
            <section className="drawer-section">
              <h4>Assigned To</h4>
              <select
                className="drawer-select"
                value={ticket.assigneeId ?? ''}
                onChange={e => {
                  const tech = technicians.find(t => t.id === e.target.value);
                  if (tech) assignTicket(ticket.ticketId, tech.id, tech.name);
                }}
              >
                <option value="">— Unassigned —</option>
                {technicians.map(t => (
                  <option key={t.id} value={t.id}>{t.avatar} {t.name} ({t.role})</option>
                ))}
              </select>
            </section>
          )}

          {/* Resolution notes (read) */}
          {ticket.resolutionNotes && (
            <section className="drawer-section">
              <h4>Resolution Notes</h4>
              <p className="drawer-resolution">{ticket.resolutionNotes}</p>
            </section>
          )}

          {/* Resolve form */}
          {showResolve && (
            <section className="drawer-section drawer-resolve-form">
              <h4>Resolution Notes <span className="required">*</span></h4>
              <textarea
                className="drawer-textarea"
                rows={3}
                placeholder="Describe what was done to resolve this issue…"
                value={resolveText}
                onChange={e => setResolveText(e.target.value)}
              />
              <div className="drawer-resolve-actions">
                <button className="btn-primary" onClick={handleResolve} disabled={!resolveText.trim()}>
                  ✅ Confirm Resolve
                </button>
                <button className="btn-secondary" onClick={() => setShowResolve(false)}>Cancel</button>
              </div>
            </section>
          )}

          {/* Action buttons */}
          {!isResolved && !showResolve && actions.length > 0 && (
            <section className="drawer-section">
              <h4>Actions</h4>
              <div className="drawer-actions">
                {actions.map(a => (
                  <button
                    key={a.next}
                    className="drawer-action-btn"
                    style={{ borderColor: a.color, color: a.color }}
                    onClick={() => handleAction(a.next)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </section>
          )}

          {/* Add note */}
          {!isResolved && (
            <section className="drawer-section">
              <h4>Add Note</h4>
              <div className="drawer-note-row">
                <input
                  className="drawer-input"
                  placeholder="Add a work note…"
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleNote()}
                />
                <button className="btn-secondary" onClick={handleNote} disabled={!noteText.trim()}>
                  Post
                </button>
              </div>
            </section>
          )}

          {/* Timeline */}
          <section className="drawer-section">
            <h4>Timeline</h4>
            <div className="ticket-timeline">
              {[...ticket.timeline].reverse().map((ev, i) => (
                <div key={i} className="timeline-event">
                  <div className="te-dot" />
                  <div className="te-body">
                    <div className="te-header">
                      <span className="te-actor">{ev.actor}</span>
                      <span className="te-time">{new Date(ev.ts).toLocaleString()}</span>
                    </div>
                    <div className="te-action">{ev.action}</div>
                    {ev.note && <div className="te-note">"{ev.note}"</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TicketDrawer;
